// Shared protocol types for WebSocket communication between client and server

// ============ Game Types ============

export interface Question {
  question: string;
  answers: string[];
  // correctIndex is NOT sent to client - server validates answers
}

export interface QuestionForClient {
  question: string;
  answers: string[];
}

export interface Player {
  playerId: string;
  nick: string;
  connected: boolean;
  joinedAt: number;
  finishedAt?: number;
  score?: number;
  totalTimeMs?: number;
  currentQuestion?: number;
}

export interface LeaderboardEntry {
  playerId: string;
  nick: string;
  score: number;
  totalTimeMs: number;
  finishedAt: number;
}

// Live progress for host race view
export interface PlayerProgress {
  playerId: string;
  nick: string;
  currentQuestion: number;
  score: number;  // Total points accumulated
  correctAnswers: number;  // Number of correct answers
  connected: boolean;
  finished: boolean;
  totalTimeMs?: number;
  hasAnsweredCurrent: boolean;  // Whether they've answered the current question
}

export type GameStatus = 'lobby' | 'running' | 'finished';

export interface GameState {
  sessionId: string;
  status: GameStatus;
  hostPlayerId: string | null;
  players: Player[];
  startedAt?: number;
  questions?: QuestionForClient[];
  leaderboard?: LeaderboardEntry[];
}

// ============ Client -> Server Messages ============

export interface HelloMessage {
  type: 'hello';
  playerId: string;
  nick: string;
}

export interface SetNickMessage {
  type: 'set_nick';
  nick: string;
}

export interface StartGameMessage {
  type: 'start_game';
}

export interface AnswerMessage {
  type: 'answer';
  questionIndex: number;
  selectedIndex: number;
}

export interface FinishedMessage {
  type: 'finished';
}

export interface PingMessage {
  type: 'ping';
}

export interface HostHelloMessage {
  type: 'host_hello';
}

export type ClientMessage =
  | HelloMessage
  | SetNickMessage
  | StartGameMessage
  | AnswerMessage
  | FinishedMessage
  | PingMessage
  | HostHelloMessage;

// ============ Server -> Client Messages ============

export interface StateMessage {
  type: 'state';
  state: GameState;
}

export interface HostAssignedMessage {
  type: 'host_assigned';
  hostPlayerId: string;
}

export interface GameStartedMessage {
  type: 'game_started';
  startedAt: number;
  questions: QuestionForClient[];
}

export interface PlayerUpdateMessage {
  type: 'player_update';
  players: Player[];
}

export interface AnswerResultMessage {
  type: 'answer_result';
  questionIndex: number;
  correct: boolean;
  correctIndex: number;
  pointsEarned: number;  // Points earned this question
  totalScore: number;    // Total accumulated score
  answerTimeMs: number;  // How fast they answered
}

export interface NextQuestionMessage {
  type: 'next_question';
  questionIndex: number;
  question: QuestionForClient;
}

export interface GameFinishedMessage {
  type: 'game_finished';
  leaderboard: LeaderboardEntry[];
}

export interface ErrorMessage {
  type: 'error';
  message: string;
}

export interface PongMessage {
  type: 'pong';
}

export interface ProgressUpdateMessage {
  type: 'progress_update';
  progress: PlayerProgress[];
  elapsedMs: number;
  currentQuestionIndex: number;
  currentQuestionText: string;  // Show question on lobby
  allAnswered: boolean;  // Whether everyone has answered current question
}

export type ServerMessage =
  | StateMessage
  | HostAssignedMessage
  | GameStartedMessage
  | PlayerUpdateMessage
  | AnswerResultMessage
  | NextQuestionMessage
  | GameFinishedMessage
  | ErrorMessage
  | PongMessage
  | ProgressUpdateMessage;

// ============ API Types ============

export interface CreateSessionResponse {
  sessionId: string;
  joinUrl: string;
}

export interface SessionInfo {
  sessionId: string;
  status: GameStatus;
  playerCount: number;
  createdAt: number;
}

// ============ Open Trivia DB Types ============

export interface OpenTriviaQuestion {
  category: string;
  type: string;
  difficulty: string;
  question: string;
  correct_answer: string;
  incorrect_answers: string[];
}

export interface OpenTriviaResponse {
  response_code: number;
  results: OpenTriviaQuestion[];
}

// ============ Internal Server Types ============

export interface StoredQuestion {
  question: string;
  answers: string[];
  correctIndex: number;
}

export interface StoredPlayer {
  playerId: string;
  nick: string;
  connected: boolean;
  joinedAt: number;
  finishedAt?: number;
  score: number;           // Total points
  correctAnswers: number;  // Number of correct answers
  totalTimeMs?: number;
  currentQuestion: number;
  answers: (number | null)[];     // Track answer index for each question
  answerTimes: (number | null)[]; // Track answer time for each question (ms from question start)
  hasAnsweredCurrent: boolean;    // Whether they've answered the current synced question
}

export interface SessionState {
  sessionId: string;
  createdAt: number;
  hostPlayerId: string | null;
  players: Map<string, StoredPlayer>;
  status: GameStatus;
  startedAt?: number;
  questions?: StoredQuestion[];
}

// ============ Constants ============

export const TOTAL_QUESTIONS = 5;
export const ROUNDS = 1;
export const QUESTIONS_PER_ROUND = 5;
export const QUESTION_TIME_LIMIT_MS = 30000; // 30 seconds
export const GAME_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
export const MAX_NICK_LENGTH = 16;
export const MIN_NICK_LENGTH = 1;

// Scoring constants
export const BASE_POINTS = 100;           // Points for correct answer
export const MAX_SPEED_BONUS = 50;        // Max bonus for fastest answer
export const MAX_POSSIBLE_SCORE = (BASE_POINTS + MAX_SPEED_BONUS) * TOTAL_QUESTIONS;
